import { computed, inject } from '@angular/core'
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals'
import { HttpClient, HttpParams } from '@angular/common/http'
import { type Observable, catchError, map, of, tap } from 'rxjs'
import { NGXLogger } from 'ngx-logger'

import type { hr } from 'efd-cdb-backend-interfaces-ts'
import { type JsonApiResponseList } from '@inside-hub-app/ef-ng-json-api'
import { HubConfigurationService } from '@inside-hub-app/hub-configuration'

import { DqMetadata } from '../models/data-quality.model'

type DqMetadataDTO = hr.emilfreydigital.cdb.dto.DqMetadataDTO

interface DqMetadataState {
  _items: DqMetadataDTO[]
}

const INITIAL_STATE: DqMetadataState = {
  _items: []
}

/**
 * In-memory store for Data Quality metadata.
 *
 * Replaces the previous storage-based caching of DQ metadata
 * (`inside_hub_app.dq_metadata.UPDATE_PROFILE` and `inside_hub_app.dq_metadata.VERIFY_EMAIL`).
 * Data is loaded from the API on application startup (via `provideDqMetadataRehydration`)
 * and after login (via `loadDqMetadata`), and kept exclusively in memory.
 */
export const DqMetadataStore = signalStore(
  { providedIn: 'root' },
  withState(INITIAL_STATE),
  withProps(() => ({
    _http: inject(HttpClient),
    _hubConfigurationService: inject(HubConfigurationService),
    _logger: inject(NGXLogger)
  })),
  withComputed(({ _items }) => ({
    items: computed(() => _items())
  })),
  withMethods(({ _items, _http, _hubConfigurationService, _logger, ...store }) => ({
    /**
     * Fetches DQ metadata for the given user from the API and stores it in memory.
     * @returns Observable that emits the loaded items (empty list on failure).
     */
    loadDqMetadata: (userId: number): Observable<DqMetadataDTO[]> => {
      if (!userId) {
        patchState(store, { _items: [] })
        return of([])
      }

      const apiUrl = _hubConfigurationService.get<string>('api.pp-url')
      const params = new HttpParams()
        .append('filter[entityId]', String(userId))
        .append('filter[entityType]', DqMetadata.USER)
        .append('filter[actionPerformed]', 'false')

      return _http.get<JsonApiResponseList<DqMetadataDTO>>(`${apiUrl}/cdb/dq-metadata`, { params }).pipe(
        map(response => response.data?.map(({ attributes }) => attributes) ?? []),
        tap(items => { patchState(store, { _items: items }) }),
        catchError(error => {
          _logger.error('[DqMetadataStore] Loading DQ metadata from API failed:', error)
          patchState(store, { _items: [] })
          return of([])
        })
      )
    },

    /**
     * Removes DQ metadata items of the given required action type from the store.
     */
    deleteByRequiredAction: (type: DqMetadata.UPDATE_PROFILE | DqMetadata.VERIFY_EMAIL): void => {
      patchState(store, { _items: _items().filter(item => item.requiredAction !== type) })
    },

    /**
     * Clears all DQ metadata from the store.
     */
    clear: (): void => {
      patchState(store, { _items: [] })
    }
  }))
)
